'use client';

import Link from 'next/link';
import Image from 'next/image';
import React from 'react';
import { profileThumb } from '@/lib/imageUrls';
import { cn } from '@/lib/utils';
import { scrubContactInfo } from '@/lib/scrubContact';
import { normalizeSlug } from '@/lib/seo/slug';
import { bucketForCategory, BUCKET_LABELS, friendlyCity } from '@/lib/posts/labels';
import PostCardSkeleton from '@/components/skeletons/PostCard';
import { MapPin, MessageSquare } from 'lucide-react';
import { producerRoleOf } from '@/lib/producerLabel';
import ProducerBadge from '@/components/common/ProducerBadge';
// NO CLIENT-SIDE GATING. The API is the ONLY source of truth for content gating.
// Guests get blurred URLs + scrubbed text from the server; authed users get originals.
// Do NOT import useSession here. Do NOT re-apply blur/scrubbing based on session status.
// See: docs/CONTENT-GATING-SPEC.md and vault/sites/juicystarz.md.

export interface PostCardProps {
  postId: number;
  title: string;
  details: string;
  stageName: string;
  avatarUrl: string;
  age: number;
  sex?: string;
  cityName: string;
  state?: string;
  postalCode?: string;
  categoryDescription: string;
  locationSlug: string;
  categorySlug: string;
  showMeta?: boolean;
  /** show the diagonal category ribbon (hide on category-specific pages) */
  showCategoryRibbon?: boolean;
  loading?: boolean;
  variant?: 'default' | 'light';
  owner?: { displayName?: string; avatarUrl?: string; userId?: string; personId?: number };
  /** @deprecated — kept only for callers that still pass it. Ignored. Gating is server-side. */
  unsafe?: boolean;
  /** Number of comments on this post; rendered as a small badge in the meta row when defined */
  commentCount?: number;
  /** ISO date string for the post's creation time; renders the "1d ago" meta line */
  createdAt?: string | Date | null;
  /** Hide the author avatar/name row (profile timelines: every card belongs to the page owner). */
  showAuthor?: boolean;
  /** B3: a paid featured casting call. Shows a visible "Sponsored" label first in the meta row. */
  sponsored?: boolean;
  /**
   * B3: the opaque public token of the paid order behind a featured card (the api's
   * placementToken, a random id, never the order number). Unused until B4's beacons key on it.
   */
  placementToken?: string;
  /**
   * The author's public Producer label (the api's authorIsProducer): plain text, a
   * self-declaration, never part of or styled like a Verified badge.
   */
  authorIsProducer?: boolean;
  /**
   * The api's authorProducerRole ("producer" or "producer_and_talent"): the badge reads
   * "Producer and talent" for both roles, else "Producer".
   */
  authorProducerRole?: string | null;
}

/** List cards hide post age beyond this many days (see timeAgo). */
const STALE_AGE_DAYS = 90;

function timeAgo(input?: string | Date | null): string | null {
  if (!input) return null;
  const t = typeof input === 'string' ? Date.parse(input) : input.getTime();
  if (!Number.isFinite(t)) return null;
  const sec = Math.max(1, Math.floor((Date.now() - t) / 1000));
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  // Stale posts (older than ~3 months) show NO age in list/card view. A board
  // full of "1y ago" / "2y ago" stamps reads as abandoned; recent stamps
  // ("3d ago", "2w ago") signal an active board. The post detail page still
  // shows full age. Threshold per product call 2026-05-28.
  if (day >= STALE_AGE_DAYS) return null;
  if (day < 7) return `${day}d ago`;
  const wk = Math.floor(day / 7);
  if (wk < 5) return `${wk}w ago`;
  const mo = Math.floor(day / 30);
  return `${mo}mo ago`;
}

export default function PostCard({
  postId,
  title,
  details: _details,
  stageName,
  avatarUrl,
  age,
  sex,
  cityName,
  postalCode: _postalCode,
  categoryDescription,
  locationSlug,
  categorySlug,
  state,
  showMeta = true,
  showCategoryRibbon = true,
  loading = false,
  variant: _variant = 'default',
  owner,
  unsafe: _unsafe = false,
  commentCount,
  createdAt,
  showAuthor = true,
  sponsored = false,
  placementToken: _placementToken,
  authorIsProducer = false,
  authorProducerRole = null,
}: PostCardProps) {
  const stageNamePreferred = owner?.displayName || stageName;
  const avatarPreferred = owner?.avatarUrl || avatarUrl;
  const thumbifyAbs = (absUrl: string): string => {
    try {
      const u = new URL(absUrl);
      const profileCdn = (process.env.NEXT_PUBLIC_CDN_PROFILE || process.env.NEXT_PUBLIC_CDN_BASE || '').replace(/\/$/, '');
      const known: string[] = [];
      if (profileCdn) { try { known.push(new URL(profileCdn).host); } catch {} }
      known.push('d14nsfeg9ivjwi.cloudfront.net');
      if (!known.includes(u.host)) return absUrl;
      const p = u.pathname;
      if (/_thumb(\.|$)/i.test(p)) return absUrl;
      const dot = p.lastIndexOf('.');
      if (dot > 0) { u.pathname = p.slice(0, dot) + '_thumb' + p.slice(dot); return u.toString(); }
      return absUrl;
    } catch { return absUrl; }
  };
  const initialImg = avatarPreferred
    ? (/^https?:\/\//i.test(avatarPreferred) ? thumbifyAbs(avatarPreferred) : profileThumb(avatarPreferred))
    : '/img/person.svg';
  const [imgSrc, setImgSrc] = React.useState<string>(initialImg);
  const [attemptedAlt, setAttemptedAlt] = React.useState(false);
  React.useEffect(() => { setImgSrc(initialImg); setAttemptedAlt(false); }, [initialImg]);
  const onImgError = React.useCallback(() => {
    if (attemptedAlt) { setImgSrc('/img/person.svg'); return; }
    (async () => {
      try {
        const raw = String(avatarUrl || '');
        if (!raw) { setImgSrc('/img/person.svg'); return; }
        let cleaned = raw.replace(/^\/+/, '');
        try {
          const u = new URL(raw);
          cleaned = u.pathname.replace(/^\/+/, '');
        } catch {}
        let resolved: string | null = null;
        try {
          const base = (process.env.NEXT_PUBLIC_API_BASE_URL || '').replace(/\/+$/, '');
          const url = `${base}/api/image/resolve?imageKey=${encodeURIComponent(cleaned)}&area=profile`;
          const res = await fetch(url, { cache: 'no-store' });
          if (res.ok) {
            const text = await res.text();
            if (text && /^https?:\/\//i.test(text)) resolved = text;
          }
        } catch {}
        if (resolved) { setImgSrc(resolved); return; }
        try {
          const nextUrl = `/media/resolve?k=${encodeURIComponent(cleaned)}&area=profile`;
          setImgSrc(nextUrl);
          return;
        } catch {}
        const api = (process.env.NEXT_PUBLIC_API_BASE_URL || '').replace(/\/+$/, '');
        const origin = typeof window !== 'undefined' ? window.location.origin.replace(/\/+$/, '') : '';
        const alt = api ? `${api}/${cleaned}` : (origin ? `${origin}/${cleaned}` : `/${cleaned}`);
        setImgSrc(alt);
      } finally {
        setAttemptedAlt(true);
      }
    })();
  }, [attemptedAlt, avatarUrl]);

  // Purely-numeric location slugs are legacy data (zip/placecode stored where
  // the metro slug belongs — e.g. "27260"); middleware hard-404s
  // /posts/{numeric}/... so linking them directly produces dead links
  // (2026-07-10, C-FABRIZZI profile). Fall back to "anywhere", which the
  // middleware post-detail validator 308s to the canonical URL.
  const safeLoc = /^\d+$/.test(String(locationSlug).trim()) ? 'anywhere' : locationSlug;
  // Link the slug URL, the post's one canonical address (middleware 308s the
  // no-slug form). A title that normalizes to nothing keeps the no-slug URL.
  const titleSlug = normalizeSlug(scrubContactInfo(title));
  const href = `/posts/${safeLoc}/${categorySlug}/${postId}${titleSlug ? `/${titleSlug}` : ''}`;
  // friendlyCity splits on hyphens too: legacy rows store the slug in the
  // city column ("New-york-city"), which the old \s+ split shipped verbatim.
  const displayCity = React.useMemo(() => friendlyCity(cityName), [cityName]);

  // Humanize before uppercasing: raw taxonomy slugs ("available-female-
  // talent-group") read as machine noise on a public card. Hyphens and
  // underscores become spaces; the "-group" taxonomy suffix is dropped.
  const categoryTag = (categoryDescription || categorySlug || '')
    .toString()
    .trim()
    .replace(/[-_]+/g, ' ')
    .replace(/\s+group$/i, '')
    .trim()
    .toUpperCase();
  // Shared resolver (lib/posts/labels) — the detail page uses the same one,
  // so a post can no longer flip buckets between its card and its detail view.
  const bucketKey = bucketForCategory(categorySlug || categoryDescription);
  const ribbonText = bucketKey ? BUCKET_LABELS[bucketKey] : null;
  const ago = timeAgo(createdAt);
  const metaParts: string[] = [];
  if (ago) metaParts.push(ago);
  if (categoryTag) metaParts.push(categoryTag);

  const niceSex = (() => {
    const s = (sex ? String(sex).trim() : '').toLowerCase();
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
  })();
  const sexInitial = niceSex ? niceSex.charAt(0).toUpperCase() : '';
  const ageSex = [age > 0 ? String(age) : '', sexInitial].filter(Boolean).join('');

  if (loading) return <PostCardSkeleton showMeta={showMeta} />;

  // Skip posts missing locationSlug or categorySlug — rendering the link with empty
  // segments produces `/posts///{postId}` which Ahrefs flagged as a duplicate.
  if (!locationSlug || !categorySlug) return null;

  return (
    <Link href={href} className="group block focus-ring">
      <article
        className={cn(
          'relative overflow-hidden rounded-2xl border border-border bg-card text-card-foreground p-5',
          'shadow-sm transition-shadow duration-200 hover:shadow-md'
        )}
      >
        {showCategoryRibbon && ribbonText && (
          <div aria-hidden className="pointer-events-none absolute top-0 right-0 h-24 w-24 overflow-hidden">
            <span className="absolute top-4 right-[-36px] w-[140px] rotate-45 bg-accent px-10 py-1 text-center text-[10px] font-bold uppercase tracking-wider text-accent-foreground shadow-md">
              {ribbonText}
            </span>
          </div>
        )}

        {/* min-h = 2 lines (text-xl/2xl @ leading-tight) so a 1-line title
            occupies the same space PostCardSkeleton reserves — the skeleton
            -> card swap was the homepage's largest layout shift. */}
        <h3 className="font-serif text-xl md:text-2xl font-semibold leading-tight tracking-tight text-foreground line-clamp-3 min-h-[3.125rem] md:min-h-[3.75rem]">
          {/* Soften scrub output: runs of asterisks in a 2xl serif headline
              read as a moderation flag; an ellipsis reads as a trim. */}
          {scrubContactInfo(title).replace(/\*{3,}/g, '…')}
        </h3>

        {/* B3: "Sponsored" is plain visible text, first in the meta row, so it is part of the
            link's accessible name. The ribbon above stays aria-hidden and unchanged. An organic
            card renders this row exactly as before. */}
        {(sponsored || metaParts.length > 0) && (
          <div className="mt-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            {sponsored && <span className="text-foreground">Sponsored</span>}
            {sponsored && metaParts.length > 0 && ' · '}
            {metaParts.join(' · ')}
          </div>
        )}

        {/* Author row: hidden on profile timelines (the page owner authored
            every card); the location + comment meta stays useful either way.
            Zero-comment counts are noise, not information — hide them. */}
        {(showAuthor || displayCity || (typeof commentCount === 'number' && commentCount > 0)) && (
          <>
            <div className="my-4 border-t border-border" />

            <div className="flex items-center gap-3">
              {showAuthor && (
                <div className="relative h-10 w-10 shrink-0 overflow-hidden rounded-full bg-muted ring-1 ring-border">
                  <Image
                    src={imgSrc}
                    alt={`${stageNamePreferred || 'Post'} avatar`}
                    fill
                    sizes="40px"
                    className="object-cover"
                    onError={onImgError as any}
                    priority={false}
                  />
                </div>
              )}
              <div className="min-w-0 flex-1">
                {showAuthor && (
                  <div className="flex items-center gap-1.5 text-sm">
                    <span className="font-semibold text-foreground truncate">
                      {scrubContactInfo(stageNamePreferred)}
                    </span>
                    {ageSex && (
                      <>
                        <span className="text-muted-foreground/60">·</span>
                        <span className="text-muted-foreground">{ageSex}</span>
                      </>
                    )}
                    {/* The Producer badge: the shared ProducerBadge, its own element. The author
                        row has no Verified badge; if one is ever added here, move this to the
                        meta row. */}
                    {authorIsProducer && (
                      <ProducerBadge role={producerRoleOf(authorProducerRole)} tone="light" className="shrink-0" />
                    )}
                  </div>
                )}
                {displayCity && (
                  <div className="mt-0.5 inline-flex items-center gap-1 text-xs text-muted-foreground">
                    <MapPin className="h-3 w-3 shrink-0" aria-hidden />
                    <span className="truncate">
                      {`${displayCity}${state ? `, ${state}` : ''}`}
                    </span>
                  </div>
                )}
              </div>
              {typeof commentCount === 'number' && commentCount > 0 && (
                <div className="inline-flex items-center gap-1 text-xs text-muted-foreground" aria-label={`${commentCount} comments`}>
                  <MessageSquare className="h-3.5 w-3.5" aria-hidden />
                  <span>{commentCount}</span>
                </div>
              )}
            </div>
          </>
        )}
      </article>
    </Link>
  );
}
